import React, { useState } from 'react';
import { Pencil, Trash2, Image as ImageIcon, X, ClipboardPlus } from 'lucide-react';
import { WorkOrderFindingsSummary } from '../WorkOrderFindingsSummary';
import { AddWorkOrderFindingsDialog } from '../AddWorkOrderFindingsDialog';
import { WorkOrderOriginLink } from '../WorkOrderOriginLink';
import { WorkOrderPrintButton } from '../WorkOrderPrintButton';
import { RequestOutcomeLink } from '../RequestOutcomeLink';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { SerializedWorkSummary } from '../SerializedWorkSummary';
import { imgSrc } from '../../lib/imgSrc';
import { getPriorityLabel } from '../../lib/priority';
import { historyTypeLabel, formatLabor } from '../../lib/historyList';

const DetailField = ({ label, value, testId }) => value ? <section className="history-detail-field">
  <h3>{label}</h3><p data-testid={testId}>{value}</p>
</section> : null;

export const HistoryRecordDetails = ({ event: e, isAdmin, onEditLog, onEditWorkOrder, onDeleteLog, onDeleteWorkOrder, onDeleteRequest }) => {
  const [photo, setPhoto] = useState(null);
  const [addingFindings, setAddingFindings] = useState(false);
  const isLog = e.event_type === 'maintenance_log';
  const isWO = e.event_type === 'completed_work_order';
  const isRequest = e.event_type === 'completed_request' && Boolean(onDeleteRequest);
  const photos = Array.isArray(e.images) ? e.images : [];
  return <div id={`history-details-${e.id}`} className="history-record-details" data-testid={`history-details-${e.id}`}>
    <div className="history-detail-main">
      <WorkOrderOriginLink record={e} testId={`history-origin-${e.id}`} />
      <DetailField label="Work performed" value={e.description || e.title} testId={`history-description-${e.id}`} />
      <DetailField label={isWO ? 'Completion notes' : 'Resolution'} value={e.resolution} testId={`history-resolution-${e.id}`} />
      <DetailField label="Notes" value={e.notes} testId={`history-notes-${e.id}`} />
      <WorkOrderFindingsSummary findings={e.findings} followUps={e.follow_ups} machineryId={e.machinery_id} testId={`history-findings-${e.id}`} />
      <DetailField label="Follow-up note" value={e.legacy_follow_up} testId={`history-legacy-follow-up-${e.id}`} />
      {(e.installation_points || []).length > 0 && <section className="history-detail-field">
        <h3>Installation points touched</h3>
        <p data-testid={`history-installation-points-${e.id}`}>{e.installation_points.map((p) => p.label).join(' · ')}</p>
      </section>}
      <DetailField label="Bulk parts used" value={e.parts_used} testId={`history-parts-${e.id}`} />
      <SerializedWorkSummary records={e.serialized_work} testId={`history-serial-work-${e.id}`} />
      {(e.follow_ups || []).length > 0 && <section className="history-detail-field" data-testid={`history-follow-ups-${e.id}`}>
        <h3>Follow-up · current status</h3>
        {e.follow_ups.map((f) => <div key={f.id} className="history-follow-up-detail" data-testid={`history-follow-up-${e.id}-${f.id}`}>
          <span className={f.status === 'open' ? 'history-attention' : ''}>{f.status === 'open' ? 'Required' : f.status === 'resolved' ? 'Closed' : f.status}</span>
          <p>{f.description}</p>
          {f.resolved_by_name && <p className="history-muted">Closed by {f.resolved_by_name}</p>}
        </div>)}
      </section>}
      {photos.length > 0 && <section className="history-detail-field">
        <h3><ImageIcon size={14} aria-hidden="true" /> Photos ({photos.length})</h3>
        <div className="history-photos">
          {photos.map((image, index) => <button key={`${index}-${image}`} type="button"
            data-testid={`history-${e.id}-image-${index}`} onClick={() => setPhoto(index)} aria-label={`Open photo ${index + 1}`}>
            <img src={imgSrc(image, { thumb: true })} alt={`Maintenance attachment ${index + 1}`} loading="lazy" />
          </button>)}
        </div>
      </section>}
    </div>
    <aside className="history-detail-meta">
      <dl>
        <div><dt>Record</dt><dd data-testid={`history-record-type-${e.id}`}>{historyTypeLabel(e)}</dd></div>
        <div><dt>Technician / recorded by</dt><dd data-testid={`history-actor-${e.id}`}>{e.actor_name || 'Not recorded'}</dd></div>
        <div><dt>Recorded labor hours</dt><dd data-testid={`history-detail-hours-${e.id}`}>{formatLabor(e.hours_spent)}</dd></div>
        {e.wo_number && <div><dt>Work order</dt><dd data-testid={`history-wo-number-${e.id}`}>#{e.wo_number}</dd></div>}
        {e.priority && <div><dt>Priority</dt><dd data-testid={`history-priority-${e.id}`}>{getPriorityLabel(e.priority)}</dd></div>}
        {e.request_outcome && <div><dt>{e.request_outcome === 'no_action' ? 'Acknowledged' : 'Created from request'}</dt><dd>
          {e.request_outcome === 'no_action' && !e.request_outcome_reference && <span className="text-xs text-slate-600">No work needed</span>}
          <RequestOutcomeLink outcome={e.request_outcome} reference={e.request_outcome_reference}
            recordId={e.request_outcome_record_id} machineryId={e.request_outcome_machinery_id}
            testId={`history-request-outcome-${e.id}`} />
        </dd></div>}
      </dl>
      {(isWO || (isAdmin && (isLog || isRequest))) && <div className="history-detail-actions">
        {isWO && <WorkOrderPrintButton workOrderId={e.raw_id} testId={`history-print-wo-btn-${e.raw_id}`} />}
        {isAdmin && isRequest && <Button variant="ghost" size="sm" data-testid={`delete-history-request-btn-${e.id}`}
          onClick={() => onDeleteRequest(e.raw_id)}><Trash2 size={14} /> Delete</Button>}
        {isAdmin && !isRequest && <>
        {isWO && <Button variant="outline" size="sm" data-testid={`history-add-findings-${e.id}`}
          onClick={() => setAddingFindings(true)}><ClipboardPlus size={14} /> Add findings</Button>}
        <Button variant="outline" size="sm" data-testid={`${isLog ? 'edit-history-btn' : 'edit-history-wo-btn'}-${e.id}`}
          onClick={() => isLog ? onEditLog(e.raw_id) : onEditWorkOrder(e.raw_id)}><Pencil size={14} /> Edit</Button>
        <Button variant="ghost" size="sm" data-testid={`${isLog ? 'delete-history-btn' : 'delete-history-wo-btn'}-${e.id}`}
          onClick={() => isLog ? onDeleteLog(e.raw_id) : onDeleteWorkOrder(e.raw_id)}><Trash2 size={14} /> Delete</Button>
        </>}
      </div>}
    </aside>
    {addingFindings && <AddWorkOrderFindingsDialog workOrderId={e.raw_id} title={e.title} onClose={() => setAddingFindings(false)} />}
    <Dialog open={photo !== null} onOpenChange={(open) => !open && setPhoto(null)}>
      <DialogContent className="history-photo-dialog" data-testid={`history-photo-dialog-${e.id}`}>
        <DialogTitle>Maintenance photo {photo !== null ? photo + 1 : ''}</DialogTitle>
        <DialogDescription>{e.equipment_name}</DialogDescription>
        {photo !== null && <img src={imgSrc(photos[photo])} alt={`Maintenance attachment ${photo + 1}`} data-testid={`history-photo-full-${e.id}`} />}
        <Button variant="outline" onClick={() => setPhoto(null)} data-testid={`history-photo-close-${e.id}`}><X size={16} /> Close photo</Button>
      </DialogContent>
    </Dialog>
  </div>;
};